import { useMakeModelContext } from '../../../../../components/SearchPage/features/makeModel/MakeModelContext';

import type { MakeModel } from '../../../../../components/SearchPage/features/makeModel/MakeModel.typed';
import { shouldShowTrims } from '../MakeModel.constants';

type UseMakeModelWriteURLArgs = {
  filterName?: string;
  searchQueryGroup?: string;
  updateMakeModels: (args: {
    makeModels?: Array<{
      make: string;
      model?: Array<string>;
      trim?: Array<string>;
    }>;
    filterName?: string;
    searchQueryGroup?: string;
    makeModelEditable?: string;
  }) => void;
  maximumAmountOfMakeModelGroupsToBeDisplayed: number;
  section?: string;
};

const useMakeModelWriteURL = (args: UseMakeModelWriteURLArgs) => {
  const {
    filterName,
    updateMakeModels,
    searchQueryGroup,
    maximumAmountOfMakeModelGroupsToBeDisplayed,
    section,
  } = args;

  const showTrims = shouldShowTrims(section);

  const { makeModels } = useMakeModelContext();

  const isStandaloneMake = (item?: { model?: Array<string> }) =>
    !item?.model ||
    item.model.length === 0 ||
    (item.model.length === 1 && item.model[0] === '');

  const hasSpecificModel = (makeModel?: MakeModel) =>
    !isStandaloneMake(makeModel);

  const hasDifferentEditableMake = (
    makeModels: Array<MakeModel>,
    makeOption: string,
  ) =>
    makeModels.some(
      ({ isEditable, make }) => isEditable && make !== makeOption,
    );

  const shouldUpdateEditableMake = (
    makeModel: MakeModel | undefined,
    hasDifferentEditable: boolean,
  ) => !hasSpecificModel(makeModel) && hasDifferentEditable;

  const shouldRemoveMake = (
    makeModel: MakeModel | undefined,
    hasDifferentEditable: boolean,
  ) => !hasSpecificModel(makeModel) && !hasDifferentEditable;

  const shouldAddNewCombination = (
    makeModel: MakeModel | undefined,
    hasDifferentEditable: boolean,
  ) => hasSpecificModel(makeModel) && !hasDifferentEditable;

  const onSelectMake = (makeOption: string) => {
    if (!makeOption) {
      selectDefaultMakeOption();
      return;
    }

    if (!makeModels) {
      updateMakeModels({
        makeModels: [{ make: makeOption }],
        filterName,
        makeModelEditable: 'make_model_0',
        searchQueryGroup,
      });
      return;
    }

    const editableMakeModel = makeModels.find(
      ({ make, isEditable }) => make === makeOption && isEditable,
    );
    const existingMakeModel = makeModels.find(
      ({ make }) => make === makeOption,
    );
    const makeExists = makeModels.some(({ make }) => make === makeOption);

    if (!makeExists) {
      checkForEditableMakeAndUpdate(makeModels, makeOption);
      return;
    }

    if (!showTrims) {
      removeMake(makeModels, makeOption);
      return;
    }

    // Handle trims feature logic
    if (editableMakeModel) {
      removeEditableMake(makeModels, makeOption);
      return;
    }

    const hasDifferentEditable = hasDifferentEditableMake(
      makeModels,
      makeOption,
    );

    if (shouldUpdateEditableMake(existingMakeModel, hasDifferentEditable)) {
      checkForEditableMakeAndUpdate(makeModels, makeOption);
    } else if (shouldRemoveMake(existingMakeModel, hasDifferentEditable)) {
      removeMake(makeModels, makeOption);
    } else if (
      shouldAddNewCombination(existingMakeModel, hasDifferentEditable)
    ) {
      addNewMakeModelCombination(makeModels, makeOption);
    } else {
      // Make has specific model(s) and there's a different editable make, update it
      checkForEditableMakeAndUpdate(makeModels, makeOption);
    }
  };

  const selectDefaultMakeOption = () => {
    // Remove the reference to the make associated with the editable group of make models, if any, from the URL
    if (makeModels) {
      const editableMake = makeModels?.find(
        ({ isEditable }) => isEditable,
      )?.make;
      if (editableMake) {
        removeEditableMake(makeModels, editableMake);
      }
    }
  };

  const removeMake = (
    mappedMakeModels: Array<{
      make: string;
      isEditable?: boolean;
    }>,
    makeOption: string,
  ) => {
    const updatedMakeModels = mappedMakeModels.filter(
      ({ make }) => make !== makeOption,
    );

    const makeModelEditableIndex =
      findMakeModelEditableIndex(updatedMakeModels);

    updateMakeModels({
      makeModels: updatedMakeModels.length > 0 ? updatedMakeModels : undefined,
      filterName,
      searchQueryGroup,
      makeModelEditable:
        makeModelEditableIndex >= 0
          ? `make_model_${makeModelEditableIndex}`
          : undefined,
    });
  };

  const removeEditableMake = (
    mappedMakeModels: Array<{
      make: string;
      isEditable?: boolean;
    }>,
    makeOption: string,
  ) => {
    const updatedMakeModels = mappedMakeModels.filter(
      ({ make, isEditable }) => !(make === makeOption && isEditable),
    );

    const makeModelEditableIndex =
      findMakeModelEditableIndex(updatedMakeModels);

    updateMakeModels({
      makeModels: updatedMakeModels.length > 0 ? updatedMakeModels : undefined,
      filterName,
      searchQueryGroup,
      makeModelEditable:
        makeModelEditableIndex >= 0
          ? `make_model_${makeModelEditableIndex}`
          : undefined,
    });
  };

  const removeSpecificMakeModelCombination = (
    mappedMakeModels: Array<{
      make: string;
      model?: Array<string>;
      trim?: Array<string>;
      isEditable?: boolean;
    }>,
    makeOption: string,
    modelOption?: string,
    trimOption?: string,
  ) => {
    const makeModelTrimSelectionIndex = mappedMakeModels.findIndex(
      ({ make, model, trim }) => {
        if (!modelOption) {
          // Removing standalone make: match make with no specific models
          return make === makeOption && isStandaloneMake({ model });
        }

        // Removing specific combination: match make and model
        const isMakeMatch = make === makeOption;
        const isModelMatch = model?.includes(modelOption);

        // Only consider trim matching when trims feature is enabled
        const isTrimMatch = showTrims
          ? !trimOption || trim?.includes(trimOption)
          : true;

        return isMakeMatch && isModelMatch && isTrimMatch;
      },
    );

    const updatedMakeModels = mappedMakeModels.filter(
      (_, index) => index !== makeModelTrimSelectionIndex,
    );

    const makeModelEditableIndex =
      findMakeModelEditableIndex(updatedMakeModels);

    updateMakeModels({
      makeModels: updatedMakeModels.length > 0 ? updatedMakeModels : undefined,
      filterName,
      searchQueryGroup,
      makeModelEditable:
        makeModelEditableIndex >= 0
          ? `make_model_${makeModelEditableIndex}`
          : undefined,
    });
  };

  const checkForEditableMakeAndUpdate = (
    mappedMakeModels: Array<MakeModel>,
    makeOption: string,
  ) => {
    const containsEditableMake = mappedMakeModels.some(
      ({ isEditable }) => isEditable,
    );
    const updatedMakeModels = containsEditableMake
      ? mappedMakeModels.map((makeModel) => {
          if (makeModel.isEditable) {
            return {
              make: makeOption,
              isEditable: true,
            };
          }
          return makeModel;
        })
      : [...mappedMakeModels, { make: makeOption, isEditable: true }];

    const makeModelEditableIndex =
      findMakeModelEditableIndex(updatedMakeModels);

    updateMakeModels({
      makeModels: updatedMakeModels.length > 0 ? updatedMakeModels : undefined,
      filterName,
      searchQueryGroup,
      makeModelEditable:
        makeModelEditableIndex >= 0
          ? `make_model_${makeModelEditableIndex}`
          : undefined,
    });
  };

  const addNewMakeModelCombination = (
    mappedMakeModels: Array<MakeModel>,
    makeOption: string,
  ) => {
    // Always add a new make-model combination when allowing duplicates
    // First, mark any existing editable make as non-editable
    const nonEditableMakeModels = mappedMakeModels.map((makeModel) => ({
      ...makeModel,
      isEditable: false,
    }));

    // Then add the new editable make
    const updatedMakeModels = [
      ...nonEditableMakeModels,
      { make: makeOption, isEditable: true },
    ];

    const makeModelEditableIndex = updatedMakeModels.length - 1;

    updateMakeModels({
      makeModels: updatedMakeModels,
      filterName,
      searchQueryGroup,
      makeModelEditable: `make_model_${makeModelEditableIndex}`,
    });
  };

  const findMakeModelEditableIndex = (makeModels: Array<MakeModel>) =>
    makeModels.findIndex(({ isEditable }) => isEditable);

  // Handle the selection of a single model when the showTrims is enabled.
  const onSelectSingleModel = (makeOption: string, modelOption: string) => {
    if (makeModels) {
      const mappedMakeModels = makeModels.map((item) => {
        // Only update the editable make-model combination
        // When clearing a model (modelOption is empty string):
        // - NEW BEHAVIOR (with trims): Only clear the model from the currently editable make-model group
        // - OLD BEHAVIOR (without trims): Clear the model from any make-model group with this make
        if (
          showTrims
            ? item.make === makeOption && item.isEditable && modelOption === ''
            : item.make === makeOption && modelOption === ''
        ) {
          return {
            ...item,
            model: undefined,
          };
        }
        // When setting a specific model (modelOption has a value):
        // - NEW BEHAVIOR (with trims): Only update the currently editable make-model group
        // - OLD BEHAVIOR (without trims): Update any make-model group with this make
        if (
          showTrims
            ? item.make === makeOption && item.isEditable
            : item.make === makeOption
        ) {
          const currentModels = item.model || [];
          const isModelAlreadySelected = currentModels.includes(modelOption);
          const mappedModels = modelOption
            ? isModelAlreadySelected
              ? undefined // Deselect if clicking same model
              : [modelOption] // Replace selection with new model
            : undefined;

          return {
            ...item,
            model: mappedModels,
            trim: undefined, // Reset trim when model changes
          };
        }
        return item;
      });

      updateMakeModels({
        makeModels: mappedMakeModels,
        filterName,
        makeModelEditable: `make_model_${mappedMakeModels.length - 1}`,
        searchQueryGroup,
      });
    }
  };

  const onSelectModel = (makeOption: string, modelOption: string) => {
    if (makeModels) {
      const mappedMakeModels = makeModels.map((item) => {
        // Only update the editable make-model combination

        if (
          showTrims
            ? item.make === makeOption && item.isEditable && modelOption === ''
            : item.make === makeOption && modelOption === ''
        ) {
          return {
            ...item,
            model: undefined,
          };
        }
        if (
          showTrims
            ? item.make === makeOption && item.isEditable
            : item.make === makeOption
        ) {
          const mappedModels = item.model?.includes(modelOption)
            ? item.model.filter((entry) => entry !== modelOption)
            : [...(item.model ?? []), modelOption];
          return {
            ...item,
            model: mappedModels.length > 0 ? mappedModels : undefined,
            trim: undefined, // Reset trim when model changes
          };
        }
        return item;
      });
      updateMakeModels({
        makeModels: mappedMakeModels,
        filterName,
        makeModelEditable: `make_model_${mappedMakeModels.length - 1}`,
        searchQueryGroup,
      });
    }
  };

  const onSelectTrim = (
    makeOption: string,
    _modelOption: string,
    trimOption: string,
  ) => {
    if (makeModels) {
      const mappedMakeModels = makeModels.map((item) => {
        // Only update the editable make-model combination
        if (
          showTrims
            ? item.make === makeOption && item.isEditable && trimOption === ''
            : item.make === makeOption && trimOption === ''
        ) {
          return {
            ...item,
            trim: undefined,
          };
        }
        if (
          showTrims
            ? item.make === makeOption && item.isEditable
            : item.make === makeOption
        ) {
          const mappedTrims = item.trim?.includes(trimOption)
            ? item.trim.filter((entry) => entry !== trimOption)
            : [...(item.trim ?? []), trimOption];
          return {
            ...item,
            trim: mappedTrims.length > 0 ? mappedTrims : undefined,
          };
        }
        return item;
      });
      updateMakeModels({
        makeModels: mappedMakeModels,
        filterName,
        makeModelEditable: `make_model_${mappedMakeModels.length - 1}`,
        searchQueryGroup,
      });
    }
  };

  const onAddSelection = () => {
    const makeModelEditable =
      makeModels &&
      makeModels?.length < maximumAmountOfMakeModelGroupsToBeDisplayed
        ? `make_model_${makeModels?.length}`
        : undefined;

    updateMakeModels({
      makeModels,
      filterName,
      searchQueryGroup,
      makeModelEditable,
    });
  };

  const onClearSelection = (make: string, model?: string, trim?: string) => {
    if (makeModels) {
      if (showTrims) {
        removeSpecificMakeModelCombination(makeModels, make, model, trim);
      } else {
        removeMake(makeModels, make);
      }
    }
  };

  return {
    onSelectMake,
    onSelectModel,
    onSelectTrim,
    onClearSelection,
    onSelectSingleModel,
    onAddSelection,
  };
};

export { useMakeModelWriteURL };
